import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { appReducer } from './reducers/appSlice';
import { uiReducer } from './reducers/uiSlice';
import { userStateReducer } from './reducers/userSlice';
import { authReducer } from './reducers/authSlice';
import { chatReducer } from './reducers/chatSlice';

import {
  persistStore,
  persistReducer,
  createTransform,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

const rootReducer = combineReducers({
  app: appReducer,
  UI: uiReducer,
  user: userStateReducer,
  auth: authReducer,
  chat: chatReducer,
});

// SECURITY: the `user` slice is persisted to localStorage, but it holds several
// long-lived platform/AI secrets (Facebook/TikTok access tokens, the AI-tracker
// API key + access token, and the user's custom OpenAI key). Persisting those
// leaves them readable by any script/XSS on the origin and lingering on disk
// after logout. This transform strips them on the way OUT to storage (inbound)
// while leaving the live in-memory store untouched, so runtime behaviour is
// unchanged and only persistence loses the secrets. On rehydrate (outbound) the
// slice comes back without them — the app re-fetches them via app-init as it
// already does after a hard reload (the whole `auth` slice is not persisted at
// all, so a re-auth/init already happens on load).
const stripUserSecretsTransform = createTransform<any, any>(
  (inboundState) => {
    if (!inboundState || typeof inboundState !== 'object') {
      return inboundState;
    }
    return {
      ...inboundState,
      profile: inboundState.profile
        ? { ...inboundState.profile, custom_openai_key: '' }
        : inboundState.profile,
      stats: inboundState.stats
        ? {
            ...inboundState.stats,
            ai_tracker_api_key: '',
            ai_tracker_access_token: '',
          }
        : inboundState.stats,
      company: inboundState.company
        ? {
            ...inboundState.company,
            settings: inboundState.company.settings
              ? {
                  ...inboundState.company.settings,
                  facebook_access_token: '',
                  tiktok_access_token: '',
                }
              : inboundState.company.settings,
          }
        : inboundState.company,
    };
  },
  (outboundState) => outboundState,
  { whitelist: ['user'] }
);

const persistConfig = {
  key: 'root',
  storage,
  version: 1,
  // SECURITY: redux-persist's option is `whitelist` (lowercase). It was
  // previously misspelled `whiteList`, which redux-persist silently ignored —
  // so the ENTIRE root state (including `auth.agency.accessToken` /
  // `auth.admin.accessToken` and the MCP setup API key) was being written to
  // localStorage, violating the "Bearer token in Redux ONLY, never localStorage"
  // invariant (see CLAUDE.md + authSlice). Only the `user` slice is persisted;
  // `auth` is intentionally NOT persisted, so sessions re-authenticate on reload.
  whitelist: ['user'],
  // Strip platform/AI secrets from the persisted `user` slice (see above).
  transforms: [stripUserSecretsTransform],
};

// Pin the state generic to RootState. Without this, the `<any, any>` transform
// added to persistConfig poisons persistReducer's state-type inference, which
// degrades `store.getState()` to `unknown` in places and breaks the production
// typecheck (react-scripts build) — even though the transform itself is fine.
const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
  persistConfig,
  rootReducer
);

const store = configureStore({
  reducer: persistedReducer,
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // serializableCheck: {
      //   ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      // },
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export default store;
